import React from 'react'

interface HighlightTextProps {
  text: string
  searchTerm: string
  className?: string
}

/**
 * Highlights search terms within text
 * @param text - The text to search within
 * @param searchTerm - The term to highlight
 * @param className - Optional CSS class for the highlight
 */
export const HighlightText: React.FC<HighlightTextProps> = ({ text, searchTerm, className = 'search-highlight' }) => {
  if (!searchTerm || !text) {
    return <>{text}</>
  }

  // Escape special regex characters in search term
  const escapedSearchTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  // Create regex for case-insensitive matching
  const regex = new RegExp(`(${escapedSearchTerm})`, 'gi')

  // Split text by search term while preserving the search term
  const parts = text.split(regex)

  return (
    <>
      {parts.map((part, index) => {
        // Check if this part matches the search term (case-insensitive)
        const isHighlight = regex.test(part)

        return isHighlight ? (
          <span key={index} className={className}>
            {part}
          </span>
        ) : (
          <span key={index}>{part}</span>
        )
      })}
    </>
  )
}

/**
 * Hook to determine if text contains search term
 */
export const useSearchMatch = (text: string, searchTerm: string): boolean => {
  if (!searchTerm || !text) return false
  return text.toLowerCase().includes(searchTerm.toLowerCase())
}

/**
 * Utility to get match score for sorting by relevance
 */
export const getSearchRelevanceScore = (text: string, searchTerm: string): number => {
  if (!searchTerm || !text) return 0

  const lowerText = text.toLowerCase()
  const lowerTerm = searchTerm.toLowerCase()

  // Exact match gets highest score
  if (lowerText === lowerTerm) return 100

  // Starts with search term gets high score
  if (lowerText.startsWith(lowerTerm)) return 80

  // Contains search term gets medium score
  if (lowerText.includes(lowerTerm)) return 60

  // No match
  return 0
}
